import React, { useMemo, useEffect } from "react";

import { Form, FormInstance, Input } from "antd";

import { InvoiceLinePayload } from "../../../../../bitmaker/schemas/Billing/Invoice";

import { formatCurrency } from "../../../../../utils";

const { useWatch } = Form;

interface TaxBreakdownEntry {
  name: string;
  rate: number;
  amount: number;
}

interface InvoiceSummaryTotalsProps {
  form: FormInstance;
}

export const InvoiceSummaryTotals: React.FC<InvoiceSummaryTotalsProps> = ({
  form,
}) => {
  const rawItems = useWatch("items", form);
  const items = useMemo(() => rawItems ?? [], [rawItems]);

  const language = useWatch("language", form);
  const currency = useWatch("currency", form);

  const amountPaid = form.getFieldValue("amountPaid") || 0;

  const labels = {
    subtotal: language === "es" ? "Subtotal" : "Subtotal",
    tax: language === "es" ? "Impuesto" : "Tax",
    total: language === "es" ? "Total" : "Total",
    amountPaid: language === "es" ? "Monto Pagado" : "Amount Paid",
    amountDue: language === "es" ? "Total a Pagar" : "Amount Due",
  };

  // Calculate Subtotal: Sum of (Quantity * Unit Price)
  const subtotal = useMemo(
    () =>
      (items ?? []).reduce((sum: number, item: InvoiceLinePayload) => {
        if (!item) {
          return sum;
        }
        const quantity = parseFloat(String(item.quantity) || "0") || 0;
        const unitPrice = parseFloat(String(item.unitPrice) || "0") || 0;
        return sum + quantity * unitPrice;
      }, 0),
    [items],
  );

  // Calculate Tax Summary
  const taxSummary = useMemo(() => {
    let totalTaxAmount = 0;
    const tempBreakdown = new Map<string, { rate: number; amount: number }>();
    const finalBreakdown: TaxBreakdownEntry[] = [];

    if (!items || items.length === 0) {
      return { totalTaxAmount: totalTaxAmount, taxBreakdown: finalBreakdown };
    }

    for (const item of items) {
      if (!item) {
        continue;
      }

      const quantity = parseFloat(String(item.quantity) || "0") || 0;
      const unitPrice = parseFloat(String(item.unitPrice) || "0") || 0;
      const lineSubtotal = quantity * unitPrice;

      for (const tax of item.appliedTaxes || []) {
        const taxLineAmount = lineSubtotal * tax.taxRate;
        totalTaxAmount += taxLineAmount;

        const current = tempBreakdown.get(tax.taxName) || {
          rate: tax.taxRate,
          amount: 0,
        };
        current.amount += taxLineAmount;
        tempBreakdown.set(tax.taxName, current);
      }
    }

    tempBreakdown.forEach((value, name) => {
      finalBreakdown.push({
        name: name,
        rate: value.rate,
        amount: Number(value.amount.toFixed(2)),
      });
    });

    return {
      totalTaxAmount: Number(totalTaxAmount.toFixed(2)),
      taxBreakdown: finalBreakdown,
    };
  }, [items]);

  const totalTaxAmount = taxSummary.totalTaxAmount;

  // Calculate Total: Subtotal + Tax Amount
  const total = subtotal + totalTaxAmount;

  // Calculate Amount Due: Total - Amount Paid
  const amountDue = total - amountPaid;

  useEffect(() => {
    form.setFieldsValue({
      amountDue: amountDue,
    });
  }, [amountDue, form]);

  return (
    <div className="col-span-12 grid grid-cols-12">
      <div className="col-start-5 col-span-6 flex justify-end">
        <div className="w-full max-w-xs space-y-1">
          {/* Subtotal */}
          <div className="flex justify-between font-medium text-base">
            <span className="text-gray-700">{labels.subtotal}</span>
            <span className="font-semibold">
              {formatCurrency(subtotal, currency, language)}
            </span>
          </div>

          {/* Tax */}
          {taxSummary.taxBreakdown.length > 0 ? (
            taxSummary.taxBreakdown.map((taxEntry) => {
              const percentage = (taxEntry.rate * 100).toFixed(0);
              const taxLabel = `${taxEntry.name} (${percentage}%)`;

              return (
                <div
                  key={taxEntry.name}
                  className="flex justify-between font-medium text-base"
                >
                  <span className="text-gray-700">{taxLabel}</span>

                  <span className="font-semibold">
                    {formatCurrency(taxEntry.amount, currency, language)}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="flex justify-between font-medium text-base">
              <span className="text-gray-700">{labels.tax}</span>
              <span className="font-semibold">
                {formatCurrency(0, currency, language)}
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="border-b border-dashed border-gray-400 py-1"></div>

          {/* Total */}
          <div className="flex justify-between font-bold text-base pt-1">
            <span className="text-gray-800">{labels.total}</span>
            <span className="font-semibold">
              {formatCurrency(total, currency, language)}
            </span>
          </div>

          {/* Amount Paid */}
          <div className="flex justify-between font-medium text-base">
            <span className="text-gray-700">{labels.amountPaid}</span>
            <span className="font-semibold">
              {formatCurrency(amountPaid, currency, language)}
            </span>
          </div>

          {/* Divider */}
          <div className="border-b-2 border-solid border-gray-800 py-1"></div>

          {/* Amount Due */}
          <div className="flex justify-between font-bold text-base pt-1">
            <span className="text-green-600">{labels.amountDue}</span>
            <span className="text-green-600">
              {formatCurrency(amountDue, currency, language)}
            </span>
          </div>
        </div>
      </div>
      <Form.Item name="amountDue" hidden noStyle>
        <Input />
      </Form.Item>
      <Form.Item name="amountPaid" hidden noStyle>
        <Input />
      </Form.Item>
    </div>
  );
};
